import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import { parseApiError } from '@/utils/errors'
import type { LoginRequest, LoginResponse, ProfileResponse, UpdateProfile, ParsedError } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const currentUser = ref<ProfileResponse | null>(null)
  const permissions = ref<string[]>([])
  const isLoading = ref(false)
  const error = ref<ParsedError | null>(null)

  const isAuthenticated = computed(() => !!accessToken.value)

  async function login(credentials: LoginRequest) {
    isLoading.value = true
    error.value = null

    try {
      // Replace with your actual API call once generated
      const response: LoginResponse = await apiClient.instance.post('/api/auth/login', credentials).then(r => r.data)

      const { accessToken: token, refreshToken: refresh, operatorId, name, roles, permissions: perms } = response

      accessToken.value = token
      refreshToken.value = refresh
      permissions.value = perms

      localStorage.setItem('accessToken', token)
      localStorage.setItem('refreshToken', refresh)

      currentUser.value = {
        id: operatorId,
        name,
        roles,
        metadata: null,
        version: 0,
        createdAt: null,
        updatedAt: null,
        permissions: perms,
      }

      return response
    } catch (err: any) {
      error.value = parseApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function logout() {
    accessToken.value = null
    refreshToken.value = null
    currentUser.value = null
    permissions.value = []

    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }

  async function fetchProfile() {
    isLoading.value = true
    error.value = null

    try {
      const response: ProfileResponse = await apiClient.operatorsMeList()
      currentUser.value = response
      // Permissions are only set at login otherwise — sync them from the
      // profile so the permission-gated UI survives a page reload
      // (spec 003 amendment 2026-09-10).
      permissions.value = response.permissions
      return response
    } catch (err: any) {
      error.value = parseApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function ensureProfile() {
    if (accessToken.value && !currentUser.value) {
      try {
        await fetchProfile()
      } catch {
        logout()
        throw new Error('Failed to fetch profile')
      }
    }
  }

  async function updateProfile(data: UpdateProfile) {
    isLoading.value = true
    error.value = null

    try {
      const response: ProfileResponse = await apiClient.operatorsMePartialUpdate(data)
      currentUser.value = response
      permissions.value = response.permissions
      return response
    } catch (err: any) {
      error.value = parseApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    accessToken,
    refreshToken,
    currentUser,
    permissions,
    isLoading,
    error,
    isAuthenticated,
    login,
    logout,
    fetchProfile,
    ensureProfile,
    updateProfile,
  }
})
